/**
 * Insights and Recommendations Layer - Vue Version
 *
 * Generates human-readable contextual insights and actionable recommendations
 * based on trait coverage analysis and match scores.
 *
 * Uses translation system instead of hardcoded locale files.
 */

/**
 * Helper: Generate behavioral insights from coverage analysis
 *
 * @param {Array} requirements - Coverage requirements array with category, signal, coveragePercent
 * @param {Object} applicantValue - Applicant strengths data
 * @param {number} matchScore - Overall match score (0-100)
 * @param {Function} $t - Vue i18n translation function
 * @returns {Object} Behavioral insights object with all narrative sections
 */
export const generateBehavioralInsights = (
	requirements,
	applicantValue,
	matchScore,
	$t
) => {
	const strengths = requirements.filter(r => r.category === 'strength') // ≥120%
	const adequate = requirements.filter(r => r.category === 'adequate') // 70-119%
	const gaps = requirements.filter(r => r.category === 'gap') // <70%

	const band = getAlignmentBand(matchScore)
	const roundedMatchScore = Math.round(Number(matchScore) || 0)

	let primaryAlignment = ''
	if (band === 'strong') {
		primaryAlignment = $t(
			'personality_results.behavioral_insights.primary_alignment.strong',
			{
				score: roundedMatchScore
			}
		)
	} else if (band === 'moderate') {
		if (gaps.length === 0) {
			primaryAlignment = $t(
				'personality_results.behavioral_insights.primary_alignment.moderate_no_gaps',
				{
					score: roundedMatchScore
				}
			)
		} else if (adequate.length > 0) {
			const adequateList = adequate
				.slice(0, 2)
				.map(a => a.signal)
				.join(' and ')
			const gapList = gaps
				.slice(0, 2)
				.map(g => g.signal)
				.join(' and ')
			primaryAlignment = $t(
				'personality_results.behavioral_insights.primary_alignment.partial_with_adequate',
				{
					score: roundedMatchScore,
					adequateList: adequateList,
					gapList: gapList
				}
			)
		} else {
			const gapList = gaps
				.slice(0, 2)
				.map(g => g.signal)
				.join(' and ')
			primaryAlignment = $t(
				'personality_results.behavioral_insights.primary_alignment.moderate_multiple_gaps',
				{
					score: roundedMatchScore,
					gapList: gapList
				}
			)
		}
	} else {
		primaryAlignment = $t(
			'personality_results.behavioral_insights.primary_alignment.poor',
			{
				score: roundedMatchScore
			}
		)
	}

	// KEY STRENGTHS - highlight adequate coverage, not just exceptional
	let keyStrengths = ''
	if (strengths.length > 0) {
		// Has traits that exceed requirements
		const strengthList = strengths.slice(0, 3).map(s =>
			$t(
				'personality_results.behavioral_insights.key_strengths.strength_item',
				{
					signal: s.signal,
					coveragePercent: s.coveragePercent,
					workplaceStyle: s.workplaceStyle
				}
			)
		)
		keyStrengths = strengthList.join(' • ')
	} else if (adequate.length > 0) {
		// Has adequate coverage but no exceptional areas
		const adequateList = adequate.slice(0, 3).map(a =>
			$t(
				'personality_results.behavioral_insights.key_strengths.adequate_item',
				{
					signal: a.signal,
					coveragePercent: a.coveragePercent,
					workplaceStyle: a.workplaceStyle
				}
			)
		)
		keyStrengths = adequateList.join(' • ')
	} else {
		// No strengths and no adequate coverage
		keyStrengths = $t(
			'personality_results.behavioral_insights.key_strengths.none'
		)
	}

	// APPLICANT ADDED VALUE - what they bring beyond requirements
	let applicantAddedValue = ''
	if (applicantValue.strengths.length > 0) {
		const complementary = applicantValue.strengths.filter(
			s => s.relevance === 'complementary'
		)
		const additional = applicantValue.strengths.filter(
			s => s.relevance === 'additional'
		)
		const overdelivers = applicantValue.strengths.filter(
			s => s.relevance === 'overdelivers'
		)

		if (overdelivers.length > 0) {
			const od = overdelivers[0]
			applicantAddedValue = $t(
				'personality_results.behavioral_insights.applicant_added_value.overdelivers_intro',
				{
					signal: od.signal
				}
			)
		}

		if (complementary.length > 0) {
			const comp = complementary[0]
			applicantAddedValue += $t(
				'personality_results.behavioral_insights.applicant_added_value.complementary_continuation',
				{
					signal: comp.signal,
					context: comp.context
				}
			)
		} else if (additional.length > 0) {
			const add = additional[0]
			const workplaceStyle = $t(
				`personality_results.trait.${add.key}.workplace_style`
			)
			applicantAddedValue += $t(
				'personality_results.behavioral_insights.applicant_added_value.additional_continuation',
				{
					signal: add.signal,
					workplaceStyle: workplaceStyle
				}
			)
		}
	} else {
		if (band !== 'weak') {
			applicantAddedValue = $t(
				'personality_results.behavioral_insights.applicant_added_value.no_added_value_good_match'
			)
		} else {
			applicantAddedValue = ''
		}
	}

	// DEVELOPMENT AREAS - be specific about the gap
	let developmentAreas = ''
	if (gaps.length > 0) {
		const topGaps = gaps.slice(0, 3)
		const gapDescriptions = topGaps.map(g => {
			const risk = getBehavioralRisk(g.key, $t)
			return $t(
				'personality_results.behavioral_insights.development_areas.gap_item',
				{
					signal: g.signal,
					coveragePercent: g.coveragePercent,
					risk: risk
				}
			)
		})
		developmentAreas = gapDescriptions.join(' • ')
	} else {
		developmentAreas = $t(
			'personality_results.behavioral_insights.development_areas.no_gaps'
		)
	}

	let recommendation = ''
	if (band === 'strong') {
		recommendation = $t(
			'personality_results.behavioral_insights.recommendation.strong'
		)
	} else if (band === 'moderate') {
		if (gaps.length === 0) {
			recommendation = $t(
				'personality_results.behavioral_insights.recommendation.moderate_no_gaps'
			)
		} else if (adequate.length >= gaps.length) {
			recommendation = $t(
				'personality_results.behavioral_insights.recommendation.partial_compensated',
				{
					gapSignal: gaps[0]?.signal || 'existing',
					strengthSignal: adequate[0]?.signal || 'existing'
				}
			)
		} else {
			recommendation = $t(
				'personality_results.behavioral_insights.recommendation.moderate_with_gaps',
				{
					gapSignal: gaps[0]?.signal || 'existing'
				}
			)
		}
	} else {
		recommendation = $t(
			'personality_results.behavioral_insights.recommendation.poor'
		)
	}

	return {
		primaryAlignment,
		keyStrengths,
		applicantAddedValue,
		developmentAreas,
		recommendation
	}
}

/**
 * Helper: Get behavioral risk description for a trait
 *
 * @param {string} traitKey - Trait identifier
 * @param {Function} $t - Vue i18n translation function
 * @returns {string} Behavioral risk description
 */
export const getBehavioralRisk = (traitKey, $t) => {
	return $t(`personality_results.behavioral_risk.${traitKey}.description`)
}

/**
 * Generate comprehensive insight summary
 *
 * @param {Object} gapAnalysis - Gap analysis result
 * @param {Object} jobSignal - Job signal data
 * @param {Object} applicantSignal - Applicant signal data
 * @param {number} matchScore - Overall match score
 * @param {Function} $t - Vue i18n translation function
 * @returns {Object} Complete insight summary
 */
export const generateInsightSummary = (
	gapAnalysis,
	jobSignal,
	applicantSignal,
	matchScore,
	$t
) => {
	// Build coverage requirements from gap analysis
	const requirements = buildCoverageRequirements(gapAnalysis, $t)

	// Build applicant value data
	const applicantValue = buildApplicantValue(applicantSignal, jobSignal, $t)

	// Generate behavioral insights
	const behavioralInsights = generateBehavioralInsights(
		requirements,
		applicantValue,
		matchScore,
		$t
	)

	// Determine match label
	const matchLabel = getMatchLabel(matchScore, $t)
	const alignmentFlags = getAlignmentFlags(matchScore)

	return {
		matchLabel,
		matchScore,
		...behavioralInsights,
		// Additional metadata for UI
		hasStrengths: requirements.some(r => r.category === 'strength'),
		hasGaps: requirements.some(r => r.category === 'gap'),
		...alignmentFlags
	}
}

/**
 * Generate actionable recommendations
 *
 * @param {Object} gapAnalysis - Gap analysis result
 * @param {Object} applicantSignal - Applicant signal data
 * @param {Function} $t - Vue i18n translation function
 * @returns {Object} Actionable recommendations
 */
export const generateRecommendations = (gapAnalysis, applicantSignal, $t) => {
	const recommendations = []

	// Good foundation recommendation
	if (gapAnalysis.primaryGap) {
		// Find the strongest adequate trait as foundation
		const adequateTraits = Object.entries(gapAnalysis.coverages)
			.filter(([trait, coverage]) => coverage >= 0.7 && coverage < 1.2)
			.sort((a, b) => b[1] - a[1])

		if (adequateTraits.length > 0) {
			const [trait, coverage] = adequateTraits[0]
			const traitName = $t(`personality_results.trait.${trait}.name`)
			const archetype = $t(`personality_results.trait.${trait}.archetype`)

			recommendations.push({
				type: 'goodFoundation',
				trait: trait,
				text: $t(
					'personality_results.recommendations.good_foundation',
					{
						trait: traitName,
						archetype: archetype
					}
				),
				priority: 'high'
			})
		}
	}

	// Development focus recommendations
	if (gapAnalysis.primaryGap) {
		const gap = gapAnalysis.primaryGap
		const archetype = $t(`personality_results.trait.${gap.trait}.archetype`)

		recommendations.push({
			type: 'developmentFocus',
			trait: gap.trait,
			text: $t('personality_results.recommendations.development_focus', {
				trait: gap.trait,
				archetype: archetype
			}),
			priority: 'high'
		})
	}

	// Additional development areas
	gapAnalysis.additionalGaps.forEach(gap => {
		const archetype = $t(`personality_results.trait.${gap.trait}.archetype`)
		recommendations.push({
			type: 'additionalDevelopment',
			trait: gap.trait,
			text: $t(
				'personality_results.recommendations.additional_development',
				{
					trait: gap.trait,
					archetype: archetype
				}
			),
			priority: 'medium'
		})
	})

	return recommendations
}

/**
 * Present insights for organization perspective
 *
 * @param {Object} rawInsights - Raw insights from generateBehavioralInsights
 * @param {number} matchScore - Overall match score
 * @param {Array} strengths - Array of strength objects
 * @param {Array} gaps - Array of gap objects
 * @param {Function} $t - Vue i18n translation function
 * @returns {Object} Organization-formatted insights
 */
export const presentForOrganization = (
	rawInsights,
	matchScore,
	strengths,
	gaps,
	$t
) => {
	const matchLabel = getMatchLabel(matchScore, $t)

	return {
		title: $t('personality_results.presentation.organization.title'),
		summary: rawInsights.primaryAlignment,
		strengths: rawInsights.keyStrengths,
		concerns: rawInsights.developmentAreas,
		recommendation: rawInsights.recommendation,
		matchScore: matchScore,
		matchLabel: matchLabel,
		metadata: {
			strengthsCount: strengths.length,
			gapsCount: gaps.length,
			hasSignificantGaps: gaps.some(gap => gap.coveragePercent < 50),
			isStrongCandidate: getAlignmentFlags(matchScore).isStrongMatch
		}
	}
}

/**
 * Present insights for applicant perspective
 *
 * @param {Object} rawInsights - Raw insights from generateBehavioralInsights
 * @param {number} matchScore - Overall match score
 * @param {Array} strengths - Array of strength objects
 * @param {Array} gaps - Array of gap objects
 * @param {Function} $t - Vue i18n translation function
 * @returns {Object} Applicant-formatted insights
 */
export const presentForApplicant = (
	rawInsights,
	matchScore,
	strengths,
	gaps,
	$t
) => {
	const getApplicantMatchLabel = (score, $t) => {
		const band = getAlignmentBand(score)

		if (band === 'strong') {
			return $t(
				'personality_results.presentation.applicant.match_labels.great_match'
			)
		}

		if (band === 'moderate') {
			return $t(
				'personality_results.presentation.applicant.match_labels.moderate_match'
			)
		}

		return $t(
			'personality_results.presentation.applicant.match_labels.development_opportunity'
		)
	}

	const matchLabel = getApplicantMatchLabel(matchScore, $t)

	// Add growth tips for gaps
	const addGrowthTips = (developmentAreas, gaps, $t) => {
		if (!developmentAreas || gaps.length === 0) {
			return $t(
				'personality_results.presentation.applicant.no_development_needed'
			)
		}

		const areasWithTips = developmentAreas
			.split(' • ')
			.map((area, index) => {
				if (gaps[index]) {
					const growthTip = $t(
						`personality_results.applicant_trait_voices.${gaps[index].key}.growth_tip`
					)
					return `${area} ${$t(
						'personality_results.presentation.applicant.growth_tip_connector',
						{ tip: growthTip }
					)}`
				}
				return area
			})

		return areasWithTips.join(' • ')
	}

	// Rewrite summary in second person
	const rewriteSummaryForApplicant = (summary, $t) => {
		return $t('personality_results.presentation.applicant.summary_prefix', {
			insights: summary
		})
	}

	// Rewrite recommendation to be more encouraging
	const rewriteRecommendationForApplicant = (
		recommendation,
		matchScore,
		$t
	) => {
		const band = getAlignmentBand(matchScore)

		if (band === 'strong') {
			return $t(
				'personality_results.presentation.applicant.recommendations.strong_fit'
			)
		}

		if (band === 'moderate') {
			return $t(
				'personality_results.presentation.applicant.recommendations.development_focus'
			)
		}

		return $t(
			'personality_results.presentation.applicant.recommendations.growth_opportunity'
		)
	}

	return {
		title: $t('personality_results.presentation.applicant.title'),
		summary: rewriteSummaryForApplicant(rawInsights.primaryAlignment, $t),
		value: rawInsights.applicantAddedValue,
		strengths: rawInsights.keyStrengths,
		development: addGrowthTips(rawInsights.developmentAreas, gaps, $t),
		recommendation: rewriteRecommendationForApplicant(
			rawInsights.recommendation,
			matchScore,
			$t
		),
		matchScore: matchScore,
		matchLabel: matchLabel,
		metadata: {
			strengthsCount: strengths.length,
			developmentAreasCount: gaps.length,
			hasGrowthOpportunities: gaps.length > 0,
			encouragesAction: getAlignmentBand(matchScore) !== 'strong'
		}
	}
}

/**
 * Context-aware presentation that adapts to available view contexts
 *
 * @param {Object} rawInsights - Raw insights from generateBehavioralInsights
 * @param {number} matchScore - Overall match score
 * @param {Array} strengths - Array of strength objects with key, name, signal, coveragePercent, workplaceStyle
 * @param {Array} gaps - Array of gap objects with key, name, signal, coveragePercent
 * @param {string} viewContext - 'applicant', 'organization', or 'both'
 * @param {Function} $t - Vue i18n translation function
 * @returns {Object} Context-appropriate presentation
 */
export const presentInsightsWithContext = (
	rawInsights,
	matchScore,
	strengths,
	gaps,
	viewContext,
	$t
) => {
	switch (viewContext) {
		case 'applicant':
			return presentForApplicant(
				rawInsights,
				matchScore,
				strengths,
				gaps,
				$t
			)
		case 'organization':
			return presentForOrganization(
				rawInsights,
				matchScore,
				strengths,
				gaps,
				$t
			)
		case 'both':
			return presentInsightsBoth(
				rawInsights,
				matchScore,
				strengths,
				gaps,
				$t
			)
		default:
			// Default to organization view for safety
			return presentForOrganization(
				rawInsights,
				matchScore,
				strengths,
				gaps,
				$t
			)
	}
}

/**
 * Get both presentations at once
 *
 * @param {Object} rawInsights - Raw insights from generateBehavioralInsights
 * @param {number} matchScore - Overall match score
 * @param {Array} strengths - Array of strength objects with key, name, signal, coveragePercent, workplaceStyle
 * @param {Array} gaps - Array of gap objects with key, name, signal, coveragePercent
 * @param {Function} $t - Vue i18n translation function
 * @returns {Object} Object with organization and applicant presentations
 */
export const presentInsightsBoth = (
	rawInsights,
	matchScore,
	strengths,
	gaps,
	$t
) => {
	return {
		organization: presentForOrganization(
			rawInsights,
			matchScore,
			strengths,
			gaps,
			$t
		),
		applicant: presentForApplicant(
			rawInsights,
			matchScore,
			strengths,
			gaps,
			$t
		)
	}
}

/**
 * Generate complete insights with context-aware presentation
 *
 * @param {Object} gapAnalysis - Gap analysis result
 * @param {Object} jobSignal - Job signal data
 * @param {Object} applicantSignal - Applicant signal data
 * @param {number} matchScore - Overall match score
 * @param {string} viewContext - 'applicant', 'organization', or 'both'
 * @param {Function} $t - Vue i18n translation function
 * @returns {Object} Complete insights with context-appropriate presentation
 */
export const generateCompleteInsights = (
	gapAnalysis,
	jobSignal,
	applicantSignal,
	matchScore,
	viewContext,
	$t
) => {
	// Build coverage requirements from gap analysis
	const requirements = buildCoverageRequirements(gapAnalysis, $t)

	// Build applicant value data
	const applicantValue = buildApplicantValue(applicantSignal, jobSignal, $t)

	// Generate behavioral insights
	const rawInsights = generateBehavioralInsights(
		requirements,
		applicantValue,
		matchScore,
		$t
	)

	// Extract strengths and gaps for presentation
	const strengths = requirements.filter(
		r => r.category === 'strength' || r.category === 'adequate'
	)
	const gaps = requirements.filter(r => r.category === 'gap')

	// Present insights with context awareness
	const presentation = presentInsightsWithContext(
		rawInsights,
		matchScore,
		strengths,
		gaps,
		viewContext,
		$t
	)

	return {
		rawInsights,
		presentation,
		metadata: {
			viewContext,
			matchScore,
			strengthsCount: strengths.length,
			gapsCount: gaps.length,
			hasDualPresentation: viewContext === 'both'
		}
	}
}

/**
 * Helper: Build coverage requirements from gap analysis
 *
 * @param {Object} gapAnalysis - Gap analysis result
 * @param {Function} $t - Vue i18n translation function
 * @returns {Array} Coverage requirements array
 */
const buildCoverageRequirements = (gapAnalysis, $t) => {
	const requirements = []

	Object.entries(gapAnalysis.coverages).forEach(([trait, coverage]) => {
		const coveragePercent = Math.round(coverage * 100)
		const signal = $t(`personality_results.trait.${trait}.workplace_signal`)
		const workplaceStyle = $t(
			`personality_results.trait.${trait}.workplace_style`
		)

		let category
		if (coverage >= 1.2) {
			category = 'strength'
		} else if (coverage >= 0.7) {
			category = 'adequate'
		} else {
			category = 'gap'
		}

		requirements.push({
			key: trait,
			signal,
			workplaceStyle,
			coveragePercent,
			category,
			coverage
		})
	})

	return requirements
}

/**
 * Helper: Build applicant value data
 *
 * @param {Object} applicantSignal - Applicant signal data
 * @param {Object} jobSignal - Job signal data
 * @param {Function} $t - Vue i18n translation function
 * @returns {Object} Applicant value data
 */
const buildApplicantValue = (applicantSignal, jobSignal, $t) => {
	const strengths = []

	Object.entries(applicantSignal).forEach(([trait, applicantValue]) => {
		const jobValue = jobSignal[trait] || 0

		if (applicantValue > 0) {
			let relevance = 'additional'

			if (jobValue > 0) {
				if (applicantValue >= jobValue * 1.2) {
					relevance = 'overdelivers'
				} else if (applicantValue >= jobValue * 0.8) {
					relevance = 'complementary'
				}
			}

			const signal = $t(
				`personality_results.trait.${trait}.workplace_signal`
			)
			const context = $t(`personality_results.trait.${trait}.context`)

			strengths.push({
				key: trait,
				signal,
				relevance,
				context,
				value: applicantValue
			})
		}
	})

	return {
		strengths,
		summary: $t('personality_results.applicant_value.summary', {
			count: strengths.length
		})
	}
}

/**
 * Inclusive Work Style bands, applied to the rounded integer the user sees.
 *
 * Strong / green: 65–100
 * Moderate / yellow: 40–64
 * Weak / red: 0–39
 */
export const MATCH_THRESHOLDS = {
	STRONG: 65,
	MODERATE: 40,
	WEAK: 0
}

export const ALIGNMENT_BAND_COLORS = {
	strong: 'green',
	moderate: 'yellow',
	weak: 'red'
}

/**
 * Map a personality match score (0-100) to its alignment band key.
 *
 * Deterministic sibling of getMatchLabel(): same thresholds, returns the bare
 * band name instead of translated copy so callers can key translation strings
 * by band (e.g. the Comparison alignment card title).
 *
 * @param {number} matchScore - Match score from match.personality_v2.score
 * @returns {'strong'|'moderate'|'weak'} Band key
 */
export const getAlignmentBand = matchScore => {
	const score = Math.round(Number(matchScore) || 0)

	if (score >= MATCH_THRESHOLDS.STRONG) {
		return 'strong'
	}

	if (score >= MATCH_THRESHOLDS.MODERATE) {
		return 'moderate'
	}

	return 'weak'
}

/**
 * Map a Work Style score to the Badge color prop for the matching band.
 *
 * @param {number} matchScore - Match score from match.percents.personality
 * @returns {'green'|'yellow'|'red'} Badge color
 */
export const getAlignmentBandColor = matchScore => {
	return ALIGNMENT_BAND_COLORS[getAlignmentBand(matchScore)]
}

/**
 * Boolean flags for the same rounded Work Style band as getMatchLabel.
 *
 * @param {number} matchScore - Match score from match.personality_v2.score
 * @returns {{isStrongMatch: boolean, isModerateMatch: boolean, needsDevelopment: boolean}}
 */
export const getAlignmentFlags = matchScore => {
	const band = getAlignmentBand(matchScore)

	return {
		isStrongMatch: band === 'strong',
		isModerateMatch: band === 'moderate',
		needsDevelopment: band === 'weak'
	}
}

/**
 * Helper: Get match label based on score
 *
 * @param {number} matchScore - Match score
 * @param {Function} $t - Vue i18n translation function
 * @returns {string} Match label
 */
export const getMatchLabel = (matchScore, $t) => {
	return $t(
		`personality_results.match_labels.${getAlignmentBand(matchScore)}`
	)
}

export const COVERAGE_CATEGORIES = {
	STRENGTH: 1.2, // ≥120%
	ADEQUATE: 0.7, // 70-119%
	GAP: 0.7 // <70%
}

// Default export for convenience
export default {
	generateBehavioralInsights,
	generateInsightSummary,
	generateRecommendations,
	presentForOrganization,
	presentForApplicant,
	presentInsightsBoth,
	presentInsightsWithContext,
	generateCompleteInsights,
	getBehavioralRisk,
	getMatchLabel,
	getAlignmentBand,
	getAlignmentBandColor,
	getAlignmentFlags,
	MATCH_THRESHOLDS,
	COVERAGE_CATEGORIES
}
